import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Table, TableHead, TableRow, TableCell, TableBody, Stack } from '@mui/material'
import { Box } from '@mui/system'
import React from 'react'
import { BoostConfig } from 'src/Libs/flow/installation/forms'
import { formatDateToHours } from 'src/Libs/flow/installation/utils'
import { UnitTypes } from 'src/Libs/types'
import { makeStyles } from 'tss-react/mui'
import { scheduleToWeekdayValues } from '../utils/boost-config-transformer'
import { WeekdayValue } from '../types'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const useStyles = makeStyles()(theme => ({
  paper: {
    paddingInline: theme.spacing(4),
    paddingBlock: theme.spacing(2),
    backgroundColor: theme.palette.shades.shade04,
    border: `1px solid ${theme.palette.shades.shade03}`,
    boxShadow: 'none',
  },
  stack: {
    gap: theme.spacing(2),
  },
  table: {
    padding: 0,
  },
  tableHeaderCell: {
    padding: 0,
    border: 0,
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

//#region Calendar
interface BoilerCalendarProps {
  boostConfig: BoostConfig
  onClickEditCalendar: () => void
  isLoading: boolean
}

/** A Table with the weekdays and their time- & temperature-settings and a button to open a edit-dialog for changing those  */
export const BoilerCalendar: React.FC<BoilerCalendarProps> = ({ boostConfig, onClickEditCalendar, isLoading }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { classes } = useStyles()

  const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } = scheduleToWeekdayValues(boostConfig)

  return (
    <Paper className={classes.paper}>
      <Stack className={classes.stack}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeaderCell}>
                <Typography variant="h5">{t('user-settings.boiler-page.calendar.weekday')}</Typography>
              </TableCell>
              <TableCell align="right" className={classes.tableHeaderCell}>
                <Typography variant="h5">{t('user-settings.boiler-page.calendar.security-temperature')}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CalendarTableRow label={t('common.monday')} weekdayValue={monday} />
            <CalendarTableRow label={t('common.tuesday')} weekdayValue={tuesday} />
            <CalendarTableRow label={t('common.wednesday')} weekdayValue={wednesday} />
            <CalendarTableRow label={t('common.thursday')} weekdayValue={thursday} />
            <CalendarTableRow label={t('common.friday')} weekdayValue={friday} />
            <CalendarTableRow label={t('common.saturday')} weekdayValue={saturday} />
            <CalendarTableRow label={t('common.sunday')} weekdayValue={sunday} />
          </TableBody>
        </Table>
        <Box className={classes.buttonBox}>
          <ButtonWithLoading
            variant="contained"
            color="primary"
            isLoading={isLoading}
            disabled={!userCanEdit}
            onClick={onClickEditCalendar}
            sx={{ width: 'auto' }}
            label={t('user-settings.boiler-page.edit-calendar')}
          />
        </Box>
      </Stack>
    </Paper>
  )
}
//#endregion

//#region TableRow
/** A TableRow for a weekday with its name and optional 'from'- & 'to'-times and an optional temperature-settings-value */
const CalendarTableRow: React.FC<{ label: string; weekdayValue: WeekdayValue }> = ({ label, weekdayValue }) => {
  const { securityTemperature, fromTime, toTime } = weekdayValue

  const fromTimeString = fromTime ? `${formatDateToHours(fromTime)} - ` : ''
  const toTimeString = toTime ? `${formatDateToHours(toTime)}` : ''
  const securityTemperatureString = securityTemperature === undefined ? '-' : `${securityTemperature}${UnitTypes.celsius}`

  return (
    <TableRow>
      <TableCell sx={{ p: 0, border: 0 }}>
        <Typography variant="caption">{`${label} ${fromTimeString} ${toTimeString}`}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ p: 0, border: 0 }}>
        <Typography variant="caption">{securityTemperatureString}</Typography>
      </TableCell>
    </TableRow>
  )
}
//#endregion
